import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Wallet, 
  ArrowRightLeft, 
  TrendingUp, 
  Shield, 
  Globe,
  CreditCard,
  ChevronRight,
  Zap,
  Lock,
  Smartphone,
  PieChart,
  Send,
  QrCode,
  Receipt,
  Landmark,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Star
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, getGoogleAuthUrl } from "../App";

const Landing = () => {
  const navigate = useNavigate();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState({ email: "", password: "" });
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [stars, setStars] = useState([]);

  // Generate stars on mount
  useEffect(() => {
    const generatedStars = [...Array(80)].map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.8 + 0.2,
      delay: Math.random() * 4,
      duration: Math.random() * 3 + 2
    }));
    setStars(generatedStars);
  }, []);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, adminData);
      if (response.data.token) {
        localStorage.setItem('stelx_token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      toast.success("Admin access granted");
      navigate("/dashboard", { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Admin login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      if (response.data.token) {
        localStorage.setItem('stelx_token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      toast.success("Welcome back!");
      navigate("/dashboard", { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (signupData.password !== signupData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (signupData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: signupData.name,
        email: signupData.email,
        password: signupData.password
      });
      if (response.data.token) {
        localStorage.setItem('stelx_token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      toast.success("Account created successfully!");
      navigate("/dashboard", { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = getGoogleAuthUrl();
  };

  // Services data
  const services = [
    {
      icon: Wallet,
      title: "Multi-Currency Wallets",
      description: "Hold and manage JMD, USD, GBP, and CAD in one place with instant conversions.",
      gradient: "from-teal-500 to-cyan-400"
    },
    {
      icon: Send,
      title: "Instant Transfers",
      description: "Send money to anyone, anywhere in seconds with low fees and real-time tracking.",
      gradient: "from-cyan-500 to-blue-400"
    },
    {
      icon: CreditCard,
      title: "Virtual Cards",
      description: "Get a virtual card for online shopping with spending controls and instant freeze.",
      gradient: "from-teal-400 to-emerald-400"
    },
    {
      icon: Receipt,
      title: "Bill Payments",
      description: "Pay utility bills, phone top-ups, and more. Set up autopay and never miss a due date.",
      gradient: "from-emerald-500 to-teal-400"
    },
    {
      icon: PieChart,
      title: "Smart Savings",
      description: "Automatic round-ups and savings pots. Watch your money grow effortlessly.",
      gradient: "from-blue-500 to-cyan-400"
    },
    {
      icon: Globe,
      title: "Global Remittance",
      description: "Send money internationally with Stellar blockchain. Fast, secure, and transparent.",
      gradient: "from-cyan-400 to-teal-500"
    }
  ];

  // Features list
  const features = [
    "Bank-grade encryption",
    "24/7 customer support",
    "Instant notifications",
    "Biometric security",
    "Multi-currency support",
    "No hidden fees"
  ];

  return (
    <div className="h-screen overflow-y-auto snap-y snap-mandatory scroll-smooth bg-[#040810]">
      {/* Animated Galaxy Background - Fixed across all sections */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {/* Deep space gradient */}
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at 20% 20%, #0d2233 0%, transparent 50%),
                       radial-gradient(ellipse at 80% 80%, #0a1929 0%, transparent 50%),
                       radial-gradient(ellipse at 50% 50%, #061118 0%, #040810 100%)`
        }} />
        
        {/* Nebula clouds */}
        <div className="absolute top-0 left-1/4 w-[800px] h-[600px] bg-gradient-to-br from-teal-500/8 via-cyan-500/5 to-transparent rounded-full blur-[150px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[500px] bg-gradient-to-tl from-cyan-500/6 via-teal-500/4 to-transparent rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-0 w-[500px] h-[400px] bg-gradient-to-r from-emerald-500/5 to-transparent rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }} />
        
        {/* Animated stars */}
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white animate-twinkle"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              boxShadow: star.size > 1.5 ? `0 0 ${star.size * 2}px rgba(255,255,255,0.3)` : 'none'
            }}
          />
        ))}
        
        {/* Shooting stars */}
        <div className="absolute top-1/4 left-1/3 w-32 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-0 animate-shooting-star" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 right-1/4 w-24 h-px bg-gradient-to-r from-transparent via-cyan-300 to-transparent opacity-0 animate-shooting-star" style={{ animationDelay: '7s' }} />
        
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(20,184,166,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(20,184,166,0.3) 1px, transparent 1px)`,
          backgroundSize: '100px 100px'
        }} />
        
        {/* Flowing energy lines */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="energyLine1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="30%" stopColor="#14b8a6" stopOpacity="0.4" />
              <stop offset="70%" stopColor="#06b6d4" stopOpacity="0.4" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
            <linearGradient id="energyLine2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.3" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>
          <path
            d="M-100,300 Q200,200 500,280 T1000,220 T1500,300 T2000,250"
            fill="none"
            stroke="url(#energyLine1)"
            strokeWidth="1.5"
            className="animate-flow"
          />
          <path
            d="M-100,500 Q300,400 600,480 T1100,420 T1600,500 T2100,450"
            fill="none"
            stroke="url(#energyLine2)"
            strokeWidth="1"
            className="animate-flow"
            style={{ animationDelay: '2s' }}
          />
        </svg>
      </div>

      {/* Navigation - Fixed */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#040810]/70 backdrop-blur-2xl border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/stelx-logo.png" 
              alt="Stelx Pay" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-bold text-xl text-white tracking-tight">
              STELX<span className="text-teal-400">PAY</span>
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-white/60 hover:text-teal-400 text-sm transition-colors">Features</a>
            <a href="#services" className="text-white/60 hover:text-teal-400 text-sm transition-colors">Services</a>
            <a href="#cards" className="text-white/60 hover:text-teal-400 text-sm transition-colors">Cards</a>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setShowAuthModal(true)}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white font-medium px-6 rounded-full shadow-lg shadow-teal-500/25"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
            <Button 
              onClick={() => setShowAdminLogin(true)}
              variant="ghost"
              size="icon"
              className="text-white/40 hover:text-teal-400 hover:bg-white/5"
              data-testid="admin-portal-btn"
            >
              <Lock className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="snap-start h-screen flex items-center relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-teal-400" />
                <span className="text-teal-300 text-sm font-medium">Coming Soon</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
                Banking for the{" "}
                <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  modern Caribbean
                </span>
              </h1>
              
              <p className="text-lg text-white/50 max-w-lg leading-relaxed">
                Pay bills, send money, save smarter - all from one powerful app. 
                Be among the first to experience StelxPay.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setShowAuthModal(true)}
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white px-8 h-12 rounded-full text-base font-medium shadow-xl shadow-teal-500/30 transition-all hover:shadow-teal-500/40 hover:scale-105"
                  data-testid="hero-get-started-btn"
                >
                  Get Early Access
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 hover:border-teal-500/50 px-8 h-12 rounded-full text-base backdrop-blur-sm"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Features
                </Button>
              </div>
              
              {/* Stats - Pre-launch highlights */}
              <div className="flex items-center gap-10 pt-8">
                <div className="text-center">
                  <p className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">4</p>
                  <p className="text-white/40 text-sm">Currencies</p>
                </div>
                <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
                <div className="text-center">
                  <p className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">14</p>
                  <p className="text-white/40 text-sm">Parishes</p>
                </div>
                <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
                <div className="text-center">
                  <p className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">0%</p>
                  <p className="text-white/40 text-sm">Hidden Fees</p>
                </div>
              </div>
            </div>
            
            {/* Right: Cards Visual */}
            <div className="relative hidden lg:block">
              <div className="relative w-full h-[550px]">
                {/* Glow effects */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-500/20 rounded-full blur-[100px]" />
                <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-cyan-500/20 rounded-full blur-[80px]" />
                
                {/* Main card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-48 rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-teal-600 shadow-2xl shadow-teal-500/40 transform rotate-[-6deg] z-20 overflow-hidden">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
                  <div className="p-6 h-full flex flex-col justify-between relative">
                    <div className="flex justify-between items-start">
                      <img src="/stelx-logo.png" alt="Stelx" className="w-10 h-10 drop-shadow-lg" />
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div>
                      <p className="text-white/70 text-xs mb-1">Card Number</p>
                      <p className="text-white text-lg tracking-[0.2em] font-mono">5235 •••• •••• 2222</p>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-white/70 text-xs">Card Holder</p>
                        <p className="text-white text-sm font-medium">STELX USER</p>
                      </div>
                      <p className="text-white font-bold text-lg tracking-tight">StelxPay</p>
                    </div>
                  </div>
                </div>
                
                {/* Secondary card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-48 rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 shadow-2xl transform rotate-[8deg] translate-y-10 translate-x-10 z-10 border border-white/10">
                  <div className="p-6 h-full flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div className="w-12 h-8 rounded bg-amber-400/80 flex items-center justify-center">
                        <div className="w-8 h-5 border border-amber-600/50 rounded-sm" />
                      </div>
                      <p className="text-white/40 font-bold tracking-wider text-sm">VISA</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Balance</p>
                      <p className="text-white text-2xl font-bold">J$125,000</p>
                    </div>
                  </div>
                </div>
                
                {/* Background card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-48 rounded-2xl bg-gradient-to-br from-cyan-900/50 to-teal-900/50 shadow-xl transform rotate-[16deg] translate-y-20 translate-x-20 z-0 border border-cyan-500/10" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-white/30 text-xs tracking-widest">SCROLL</span>
          <div className="w-6 h-10 rounded-full border-2 border-teal-500/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-teal-400 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="snap-start min-h-screen flex items-center py-24 relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Feature list */}
            <div className="space-y-8">
              <div>
                <p className="text-teal-400 text-sm font-medium tracking-wider mb-4">WHY STELXPAY</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
                  The smartest way to move money across the{" "}
                  <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Caribbean</span>
                </h2>
              </div>
              
              <p className="text-white/50 text-lg leading-relaxed">
                StelxPay combines everything you need - payments, savings, investments, 
                and global transfers - in one beautifully simple app.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-white/70 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white px-8 h-12 rounded-full shadow-lg shadow-teal-500/25"
              >
                Explore Platform
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Right: App preview mockup */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 rounded-3xl blur-3xl" />
              <div className="relative bg-gradient-to-br from-white/[0.03] to-white/[0.01] rounded-3xl p-8 border border-white/[0.08] backdrop-blur-sm">
                {/* Mock app screen */}
                <div className="bg-[#0a1520] rounded-2xl p-6 space-y-6 border border-white/[0.05]">
                  <div className="flex items-center justify-between">
                    <span className="text-teal-400 text-sm font-medium">Send Money</span>
                    <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center">
                      <Send className="w-4 h-4 text-teal-400" />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                      <span className="text-white font-bold">TW</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Theresa Webb</p>
                      <p className="text-white/40 text-sm">NCB •••• 4523</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-white/40 text-sm mb-2">Amount</p>
                    <p className="text-4xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">$2,500.00</p>
                  </div>
                  
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 h-12 rounded-xl font-medium">
                    Send Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                
                {/* Stats preview */}
                <div className="mt-6 bg-[#0a1520] rounded-2xl p-6 border border-white/[0.05]">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white/60 text-sm">This Month</span>
                    <span className="text-teal-400 text-xs">View All</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-2">
                        <TrendingUp className="w-5 h-5 text-emerald-400" />
                      </div>
                      <p className="text-white/40 text-xs">Income</p>
                      <p className="text-white font-bold text-lg">$5,240</p>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-gradient-to-br from-rose-500/10 to-pink-500/10 border border-rose-500/20">
                      <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-2">
                        <ArrowRightLeft className="w-5 h-5 text-rose-400" />
                      </div>
                      <p className="text-white/40 text-xs">Spent</p>
                      <p className="text-white font-bold text-lg">$1,180</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="snap-start min-h-screen flex items-center py-24 relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="text-center mb-16">
            <p className="text-teal-400 text-sm font-medium tracking-wider mb-4">SERVICES</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Everything you need,{" "}
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">one app</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              From daily payments to long-term savings, StelxPay has you covered.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="group p-6 rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.06] hover:border-teal-500/30 transition-all duration-500 hover:transform hover:scale-[1.02] hover:shadow-xl hover:shadow-teal-500/10"
                  data-testid={`service-${index}`}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:shadow-xl transition-shadow`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <a href="#" className="inline-flex items-center text-teal-400 text-sm font-medium hover:text-teal-300 transition-colors group-hover:translate-x-1 transform transition-transform">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cards Section */}
      <section id="cards" className="snap-start min-h-screen flex items-center py-24 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Card visual */}
            <div className="relative order-2 lg:order-1">
              <div className="relative w-full aspect-square max-w-lg mx-auto">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/30 to-cyan-500/30 rounded-full blur-[100px] scale-75" />
                
                {/* Main card - tilted */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 sm:w-80 aspect-[1.6] rounded-2xl bg-gradient-to-br from-[#0f2027] via-[#203a43] to-[#2c5364] shadow-2xl transform hover:rotate-0 rotate-[-8deg] transition-transform duration-500 z-10 border border-white/10 overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative p-6 h-full flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <img src="/stelx-logo.png" alt="Stelx" className="w-8 h-8 opacity-80" />
                      <span className="text-white/40 text-xs font-medium tracking-wider">PREMIUM</span>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Card Number</p>
                      <p className="text-white tracking-[0.15em] font-mono text-sm">5235 4200 2432 2222</p>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-white/40 text-xs">Expires</p>
                        <p className="text-white text-sm">12/28</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">StelxPay</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-20 right-10 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-500/30 backdrop-blur-sm animate-float">
                  <span className="text-teal-400 text-sm font-medium">+$500 Cashback</span>
                </div>
                <div className="absolute bottom-24 left-5 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 backdrop-blur-sm animate-float" style={{ animationDelay: '1s' }}>
                  <span className="text-emerald-400 text-sm font-medium">0% FX Fees</span>
                </div>
              </div>
            </div>
            
            {/* Right: Text */}
            <div className="space-y-8 order-1 lg:order-2">
              <div>
                <p className="text-teal-400 text-sm font-medium tracking-wider mb-4">VIRTUAL CARDS</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
                  The card that{" "}
                  <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">works for you</span>
                </h2>
              </div>
              
              <p className="text-white/50 text-lg leading-relaxed">
                Get instant virtual cards for secure online shopping. Control spending limits, 
                freeze instantly, and earn rewards on every purchase.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-teal-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Bank-Grade Security</h4>
                    <p className="text-white/50 text-sm">256-bit encryption with instant freeze controls</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Global Acceptance</h4>
                    <p className="text-white/50 text-sm">Use your card anywhere Visa is accepted worldwide</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Instant Rewards</h4>
                    <p className="text-white/50 text-sm">Earn cashback on every transaction automatically</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white px-8 h-12 rounded-full font-medium shadow-lg shadow-teal-500/25"
              >
                Get Your Card
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="snap-start min-h-screen flex items-center py-24 relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-t from-teal-500/20 via-cyan-500/10 to-transparent rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 mb-8">
            <Star className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm">Join the Waitlist</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to take control of your{" "}
            <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">finances?</span>
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
            Join the financial revolution. Fast transfers, smart savings, 
            and complete control - all in the palm of your hand.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <Button 
              onClick={() => setShowAuthModal(true)}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white px-10 h-14 rounded-full text-lg font-medium shadow-xl shadow-teal-500/30 transition-all hover:shadow-teal-500/40 hover:scale-105"
            >
              Join Waitlist
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          {/* Features - Pre-launch */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl mx-auto">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
              <p className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">4</p>
              <p className="text-white/40 text-sm mt-1">Currencies</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
              <p className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Instant</p>
              <p className="text-white/40 text-sm mt-1">Transfers</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
              <p className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">14</p>
              <p className="text-white/40 text-sm mt-1">Parishes</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
              <p className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">24/7</p>
              <p className="text-white/40 text-sm mt-1">Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="snap-start py-16 border-t border-white/5 relative">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stelx-logo.png" alt="Stelx" className="w-8 h-8 object-contain" />
                <span className="font-bold text-white">STELX<span className="text-teal-400">PAY</span></span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed">
                The next-generation digital wallet for Jamaica and the Caribbean.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Features</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Pricing</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Security</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Business</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">About</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Careers</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Press</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Terms</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Privacy</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Cookies</a></li>
                <li><a href="#" className="text-white/40 text-sm hover:text-teal-400 transition-colors">Licenses</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-sm">© 2026 StelxPay. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-white/30 hover:text-teal-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="text-white/30 hover:text-teal-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="#" className="text-white/30 hover:text-teal-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Login Dialog */}
      <Dialog open={showAdminLogin} onOpenChange={setShowAdminLogin}>
        <DialogContent className="bg-[#0a1520] border-white/[0.08] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-center text-white flex items-center justify-center gap-2">
              <Lock className="w-5 h-5 text-teal-400" />
              Admin Portal
            </DialogTitle>
            <DialogDescription className="text-center text-white/50">
              Secure access for administrators only
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAdminLogin} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="admin-email" className="text-white/60 text-sm">Admin Email</Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                placeholder="admin@stelxpay.com"
                className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 mt-2"
                value={adminData.email}
                onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-white/60 text-sm">Password</Label>
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                placeholder="••••••••"
                className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 mt-2"
                value={adminData.password}
                onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                required
              />
            </div>
            <Button 
              data-testid="admin-login-btn"
              type="submit" 
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white h-11 rounded-xl"
              disabled={loading}
            >
              {loading ? "Authenticating..." : "Access Admin Portal"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="bg-[#0a1520] border-white/[0.08] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-center text-white">
              Welcome to StelxPay
            </DialogTitle>
            <DialogDescription className="text-center text-white/50">
              Sign in or create an account to get started
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="signin" className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-white/[0.04] rounded-xl">
              <TabsTrigger 
                value="signin" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white text-white/60 rounded-lg"
                data-testid="signin-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="signup"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white text-white/60 rounded-lg"
                data-testid="signup-tab"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin" className="space-y-4 mt-4">
              <Button
                type="button"
                variant="outline"
                className="w-full border-white/[0.08] text-white hover:bg-white/5 h-11 flex items-center justify-center gap-3 rounded-xl"
                onClick={handleGoogleLogin}
                data-testid="google-signin-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/[0.08]"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#0a1520] px-2 text-white/40">Or</span>
                </div>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    placeholder="Email"
                    className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 rounded-xl"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Input
                    data-testid="login-password-input"
                    type="password"
                    placeholder="Password"
                    className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 rounded-xl"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                </div>
                <Button 
                  data-testid="login-submit-btn"
                  type="submit" 
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white h-11 rounded-xl"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup" className="space-y-4 mt-4">
              <Button
                type="button"
                variant="outline"
                className="w-full border-white/[0.08] text-white hover:bg-white/5 h-11 flex items-center justify-center gap-3 rounded-xl"
                onClick={handleGoogleLogin}
                data-testid="google-signup-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/[0.08]"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#0a1520] px-2 text-white/40">Or</span>
                </div>
              </div>
              
              <form onSubmit={handleSignup} className="space-y-4">
                <Input
                  data-testid="signup-name-input"
                  type="text"
                  placeholder="Full Name"
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 rounded-xl"
                  value={signupData.name}
                  onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                  required
                />
                <Input
                  data-testid="signup-email-input"
                  type="email"
                  placeholder="Email"
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 rounded-xl"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  required
                />
                <Input
                  data-testid="signup-password-input"
                  type="password"
                  placeholder="Password (min 6 characters)"
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 rounded-xl"
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  required
                />
                <Input
                  data-testid="signup-confirm-input"
                  type="password"
                  placeholder="Confirm Password"
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 rounded-xl"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                  required
                />
                <Button 
                  data-testid="signup-submit-btn"
                  type="submit" 
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white h-11 rounded-xl"
                  disabled={loading}
                >
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
              
              <p className="text-xs text-white/40 text-center">
                By signing up, you agree to our Terms & Privacy Policy
              </p>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Custom styles for animations */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes shooting-star {
          0% { transform: translateX(0) translateY(0) rotate(-45deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateX(300px) translateY(300px) rotate(-45deg); opacity: 0; }
        }
        @keyframes flow {
          0% { stroke-dashoffset: 1000; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }
        .animate-shooting-star {
          animation: shooting-star 3s ease-out infinite;
        }
        .animate-flow {
          stroke-dasharray: 200;
          animation: flow 8s linear infinite;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Landing;
